/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { PhraseListTable } from 'src/luis/appShell/buildPane/featuresPage/components/PhraseListTable';
import { PhraseListTableCommandBar } from 'src/luis/appShell/buildPane/featuresPage/components/PhraseListTableCommandBar';
import { useFeatureUsedByModels } from 'src/luis/appShell/buildPane/featuresPage/useFeatureUsedByModels';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { PhraseListModalContainer } from 'src/luis/modules/features/components/PhraseListModalContainer';
import { usePhraseLists } from 'src/luis/modules/features/hooks/usePhraseLists';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';

const defaultModalsState = {
    isCreateNewPhraseListHidden: true,
    isDeletePhraseListModalHidden: true
};

const messages = defineMessages({
    pageTitle: {
        id: 'luis.featuresPage.pageTitle',
        defaultMessage: 'ML Features'
    },
    pageTitleDescription: {
        id: 'luis.featuresPage.pageTitleDescription',
        defaultMessage: 'A group of values (words or phrases) that belong to the same class and must be treated similarly.'
    },
    deletePhraseListModalTitle: {
        id: 'luis.featuresPage.deletePhraseListModalTitle',
        defaultMessage: 'Delete feature(s)?'
    },
    deletePhraseListModalDescription: {
        id: 'luis.featuresPage.deletePhraseListModalDescription',
        defaultMessage: 'Are you sure you want to delete the selected feature(s)?'
    },
    deletePhraseListsAccessibilityConfirmation: {
        id: 'luis.featuresPage.deletePhraseListsAccessibilityConfirmation',
        defaultMessage: 'Successfully deleted feature(s)'
    }
});

/**
 * Represents a page container component for the phrase lists.
 */
export const FeaturesPage = observer(() => {
    const { selection, selectedItems } = useSelection<PhraseList>();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const versionedAppId = useCurrentVersionedAppId();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const initData = { name: '', phrases: [] as string[], isExchangeable: true, id: null, isActive: true, isGlobal: true } as PhraseList;
    const [selectedPhraseList, setSelectedPhraseList] = React.useState<PhraseList>(initData);
    const { phraseLists, isLoading } = usePhraseLists();

    const [sortKey, setSortKey] = React.useState<SortKeyType<PhraseList>>({ key: 'name', order: 'asc' });
    const { announcedComponent, announceAction } = useAnnounced();
    const features = useFeatureUsedByModels<PhraseList>(orderBy(phraseLists, [sortKey.key], [sortKey.order]));

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    const deletePhraseListsTracker = useProgressTracker({
        onComplete: () => announceAction(localize(messages.deletePhraseListsAccessibilityConfirmation))
    });

    const [query, setQuery] = React.useState('');
    const filteredFeatures = orderBy(
        substringSearchStrategy(features.slice(), query, null, (item, q) => item.name.toLowerCase().indexOf(q) !== -1),
        [sortKey.key],
        [sortKey.order]
    );

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredFeatures, 10);
    const eventLoggerRef = React.useRef(getEventLogger('FeaturesPage'));

    /**
     * Fires when delete phrase list operation called
     */
    const deletePhraseLists = () => {
        selection.setAllSelected(false);
        setModalsState({ isDeletePhraseListModalHidden: true });

        selectedItems.forEach(({ id }) => {
            dispatcher.dispatch('deletePhraseList', { ...versionedAppId, id, tracker: deletePhraseListsTracker });
        });
    };

    /**
     * Fires when make phrase list global operation called to make
     * this features used by all models
     */
    const makePhraseListGlobal = () => {
        selection.setAllSelected(false);
        selectedItems.forEach(item => {
            if (!item.isGlobal) {
                dispatcher.dispatch('updatePhraseList', { phraseList: { ...item, isGlobal: true } });
            }
        });
    };

    const openPhraseListDialog = () => {
        setModalsState({ isCreateNewPhraseListHidden: false });
        eventLoggerRef.current.start('PhraseListDialog');
    };

    const submitPhraseListDialog = () => {
        eventLoggerRef.current.end('PhraseListDialog');
        selection.setAllSelected(false);
        setModalsState({ isCreateNewPhraseListHidden: true });
        setSelectedPhraseList(null);
    };

    const dismissPhraseListDialog = () => {
        eventLoggerRef.current.end('PhraseListDialog');
        eventLoggerRef.current.log('PhraseListDialogDismissed');
        setModalsState({ isCreateNewPhraseListHidden: true });
        selection.setAllSelected(false);
        setSelectedPhraseList(null);
    };

    const onQueryChange = (searchQuery: string) => {
        eventLoggerRef.current.log('FeatureTableSearchTriggered');
        setQuery(searchQuery);
    };

    return (
        <Page size="container-fluid" pageName="FeaturesPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} description={localize(messages.pageTitleDescription)} />

            {announcedComponent}

            {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}

            <PhraseListTableCommandBar
                query={query}
                onQueryChange={onQueryChange}
                selectedItems={selectedItems}
                onDeletePhraseLists={() => setModalsState({ isDeletePhraseListModalHidden: false })}
                onCreatePhraseList={openPhraseListDialog}
                onMakePhraseListGlobal={makePhraseListGlobal}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <PhraseListTable
                sortKey={sortKey}
                items={pageItems}
                selection={selection}
                isLoading={isLoading}
                areItemsFiltered={Boolean(query)}
                overallItemsCount={filteredFeatures.length}
                onPhraseListClick={phraseList => {
                    setSelectedPhraseList(phraseList);
                    setModalsState({ isCreateNewPhraseListHidden: false });
                }}
                onDeleteKeyUp={() => canDeleteData && setModalsState({ isDeletePhraseListModalHidden: false })}
                onSortColumn={c => {
                    setSortKey({ key: c.key as keyof PhraseList, order: c.isSortedDescending ? 'asc' : 'desc' });
                    selection.setAllSelected(false);
                }}
            />

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />

            <DeletePromptModal
                hidden={modalsState.isDeletePhraseListModalHidden}
                onDelete={deletePhraseLists}
                dialogContentProps={{
                    title: localize(messages.deletePhraseListModalTitle),
                    subText: localize(messages.deletePhraseListModalDescription)
                }}
                onDismiss={() => {
                    setModalsState({ isDeletePhraseListModalHidden: true });
                }}
            />

            <PhraseListModalContainer
                initData={selectedPhraseList}
                onSubmit={submitPhraseListDialog}
                onDismiss={dismissPhraseListDialog}
                hidden={modalsState.isCreateNewPhraseListHidden}
            />
        </Page>
    );
});
